import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Text, Stack, Center, Image } from "@mantine/core";
import QRCode from "qrcode";

import { UnsplashImage } from "@/types/unsplashTypes";
import { ContentBlock, Venue } from "@/utils/api";
import { UnsplashService } from "@/utils/api/UnsplashService";

import { useContentBlockDynamicContent } from "@/hooks/useContentBlockDynamicContent";
import { useContentBlockState } from "@/contexts/ContentBlockStateContext";

import { LoadingIndicator } from "@/components/generic/LoadingIndicator";
import { ContentBlockTransition } from "@/components/generic/ContentBlockTransition";
import { BlockTransitionAnimationType } from "@/components/features/BlockTransitionAnimationSelector";

// ─── State Keys ───────────────────────────────────────────────────────────────

const STATE_KEY_IMAGE_INDEX = "imageIndex";
const STATE_KEY_PREFETCH_DONE = "prefetchDone";

/**
 * How many images before the end of the loaded page to trigger a background
 * fetch of the next page.
 */
const PREFETCH_THRESHOLD = 4;

/** How many upcoming images to push into the browser image cache. */
const PRELOAD_COUNT = 3;

/**
 * Delay in ms before re-enabling a failed prefetch attempt.
 * Prevents hammering the API when fetchNext() fails repeatedly.
 */
const PREFETCH_RETRY_DELAY_MS = 5_000;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface UnsplashCollectionContentBlockProps {
  contentBlock: ContentBlock;
  className?: string;
  themeConfig?: any;
  venue?: Venue;
  transitionType?: BlockTransitionAnimationType;
  transitionDuration?: number;
  onEnded?: () => void;
  onLoad?: () => void;
  onError?: (error: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const UnsplashCollectionContentBlock: React.FC<
  UnsplashCollectionContentBlockProps
> = ({
  contentBlock,
  className,
  venue,
  transitionType = "fade",
  transitionDuration = 500,
  onEnded,
  onLoad,
  onError,
}) => {
  // ── Parse blockSettings ──────────────────────────────────────────────────

  const blockSettings = useMemo(() => {
    try {
      return contentBlock.blockSettings
        ? JSON.parse(contentBlock.blockSettings)
        : {};
    } catch {
      return {};
    }
  }, [contentBlock.blockSettings]);

  const mode: "cycle_all" | "one_per_pass" =
    blockSettings.mode === "one_per_pass" ? "one_per_pass" : "cycle_all";

  const secondsPerImage = Math.max(contentBlock.durationSeconds ?? 10, 1);

  // ── Persistent state ─────────────────────────────────────────────────────

  const { getBlockState, setBlockState } = useContentBlockState();

  const getPersistedIndex = useCallback(
    (): number => getBlockState(contentBlock.id!)[STATE_KEY_IMAGE_INDEX] ?? 0,
    [getBlockState, contentBlock.id],
  );

  const isPrefetchDone = useCallback(
    (): boolean =>
      getBlockState(contentBlock.id!)[STATE_KEY_PREFETCH_DONE] ?? false,
    [getBlockState, contentBlock.id],
  );

  // ── Dynamic content hook ─────────────────────────────────────────────────

  const {
    items,
    hasMore,
    loading,
    isLoaded,
    fetch,
    fetchNext,
    lastError,
    retryCount,
    nextRetryIn,
  } = useContentBlockDynamicContent();

  // ── Local state ───────────────────────────────────────────────────────────

  const [localIndex, setLocalIndex] = useState<number>(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const advancingRef = useRef(false);
  const trackedPhotoIds = useRef<Set<string>>(new Set());
  const prefetchingRef = useRef(false);

  // ── Load timeout — yield slot if initial fetch is too slow ───────────────
  //
  // Fires onEnded() if isLoaded hasn't become true within one image-slot
  // (secondsPerImage). This keeps the rotation alive in both modes:
  //
  //   cycle_all    — we've wasted one image-slot waiting; yield and let the
  //                  retry logic (in the hook) recover in the background.
  //                  Next time this block appears the images will likely be
  //                  cached already.
  //
  //   one_per_pass — the entire block budget IS secondsPerImage, so yielding
  //                  after that duration is exactly correct.
  //
  // The effect is intentionally only triggered once on mount (empty-ish dep
  // array). If isLoaded becomes true before the timeout, the cleanup clears
  // it and nothing happens. If the block is remounted while data is already
  // cached, isLoaded will be true immediately and the effect exits early.

  useEffect(() => {
    if (isLoaded) return;

    const timeout = setTimeout(() => {
      onEnded?.();
    }, secondsPerImage * 1000);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);
  // NOTE: intentionally omitting secondsPerImage and onEnded from deps.
  // This timeout is a one-shot budget set at mount time. Restarting it every
  // time the parent re-renders onEnded (a common inline arrow function) would
  // reset the budget and potentially stall the rotation indefinitely.

  // ── Initial fetch ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!contentBlock.id) return;
    fetch(contentBlock.id, blockSettings.perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentBlock.id]);

  // ── Restore persisted index once items are loaded ─────────────────────────

  useEffect(() => {
    if (!isLoaded || items.length === 0) return;

    const safeIndex = Math.min(getPersistedIndex(), items.length - 1);
    setLocalIndex(safeIndex);
    setImageLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // ── Current image ─────────────────────────────────────────────────────────

  const currentImage: UnsplashImage | null = items[localIndex] ?? null;

  const currentImageRef = useRef<UnsplashImage | null>(currentImage);
  currentImageRef.current = currentImage;

  // ── Background prefetch of next page ─────────────────────────────────────

  useEffect(() => {
    if (!isLoaded || !hasMore) return;
    if (prefetchingRef.current || isPrefetchDone()) return;

    const imagesRemaining = items.length - localIndex;
    if (imagesRemaining > PREFETCH_THRESHOLD) return;

    prefetchingRef.current = true;
    setBlockState(contentBlock.id!, { [STATE_KEY_PREFETCH_DONE]: true });

    fetchNext()
      .catch((err) => {
        console.warn("[UnsplashCollection] Prefetch failed:", err);
        // Delay re-enabling prefetch to avoid rapid retry loops on failure
        setTimeout(() => {
          prefetchingRef.current = false;
          setBlockState(contentBlock.id!, {
            [STATE_KEY_PREFETCH_DONE]: false,
          });
        }, PREFETCH_RETRY_DELAY_MS);
      })
      .finally(() => {
        // Only clear the in-flight guard on success here; failure path above
        // clears it after the delay instead.
        if (!prefetchingRef.current) return;
        prefetchingRef.current = false;
      });
  }, [
    localIndex,
    items.length,
    isLoaded,
    hasMore,
    fetchNext,
    isPrefetchDone,
    setBlockState,
    contentBlock.id,
  ]);

  // ── Advance logic ─────────────────────────────────────────────────────────

  const advance = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;

    try {
      const nextIndex = localIndex + 1;

      if (nextIndex < items.length) {
        setBlockState(contentBlock.id!, {
          [STATE_KEY_IMAGE_INDEX]: nextIndex,
        });
        setLocalIndex(nextIndex);
        setImageLoaded(false);

        if (mode === "one_per_pass") {
          onEnded?.();
        }
      } else {
        setBlockState(contentBlock.id!, {
          [STATE_KEY_IMAGE_INDEX]: 0,
          [STATE_KEY_PREFETCH_DONE]: false,
        });
        setLocalIndex(0);
        setImageLoaded(false);
        onEnded?.();
      }
    } finally {
      advancingRef.current = false;
    }
  }, [localIndex, items.length, setBlockState, contentBlock.id, mode, onEnded]);

  // ── Internal timer ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isLoaded || items.length === 0 || !imageLoaded) return;

    timerRef.current = setTimeout(() => {
      advance();
    }, secondsPerImage * 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    isLoaded,
    items.length,
    imageLoaded,
    localIndex,
    secondsPerImage,
    advance,
  ]);

  // ── Preload upcoming images into browser cache ────────────────────────────

  useEffect(() => {
    if (!isLoaded || items.length === 0) return;

    for (let i = 1; i <= PRELOAD_COUNT; i++) {
      const nextIndex = localIndex + i;
      if (nextIndex >= items.length) break;
      const preloader = new window.Image();
      preloader.src = items[nextIndex].urls.regular;
    }
  }, [localIndex, isLoaded, items]);

  // ── QR code for photographer attribution ─────────────────────────────────

  const userProfileUrl = useMemo(() => {
    if (!currentImage?.user?.username || !venue?.name) return null;
    const venueName = encodeURIComponent(venue.name);
    return `https://unsplash.com/@${currentImage.user.username}?utm_source=venueboards&utm_medium=display&utm_campaign=${venueName}`;
  }, [currentImage?.user?.username, venue?.name]);

  useEffect(() => {
    if (!userProfileUrl) {
      setQrCodeDataUrl("");
      return;
    }

    QRCode.toDataURL(userProfileUrl, {
      width: 80,
      margin: 1,
      color: {
        dark: "#FFFFFF",
        light: "#00000000",
      },
    })
      .then(setQrCodeDataUrl)
      .catch((error) => {
        console.error("[UnsplashCollection] QR code generation failed:", error);
        setQrCodeDataUrl("");
      });
  }, [userProfileUrl]);

  // ── Image event handlers ──────────────────────────────────────────────────

  const handleImageError = useCallback(() => {
    onError?.("Failed to load image");
    advance();
  }, [onError, advance]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    onLoad?.();

    const image = currentImageRef.current;
    if (!image?.links?.downloadLocation) return;
    if (trackedPhotoIds.current.has(image.id)) return;

    trackedPhotoIds.current.add(image.id);
    UnsplashService.trackDownload(image.links.downloadLocation).catch(
      (error) => {
        console.warn(
          "[UnsplashCollection] Failed to track download:",
          image.id,
          error,
        );
      },
    );
  }, [onLoad]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && !isLoaded) {
    return (
      <Center
        h="100%"
        className={`unsplash-collection-block unsplash-collection-block--loading ${className || ""}`}
      >
        <Stack align="center" gap="md">
          <LoadingIndicator size="lg" />
          <Text size="sm" c="dimmed">
            Loading collection...
          </Text>
        </Stack>
      </Center>
    );
  }

  // Error state — shown only while waiting for a retry. Unlike the original
  // static Alert, this screen is self-healing: the hook will retry
  // automatically and isLoaded will become true, replacing this view.
  // onEnded is NOT called here — the load timeout effect above handles
  // yielding the slot if the block takes too long.
  if (lastError && !isLoaded) {
    return (
      <Center
        h="100%"
        className={`unsplash-collection-block unsplash-collection-block--error ${className || ""}`}
      >
        <Stack align="center" gap="xs">
          <LoadingIndicator size="sm" />
          <Text size="sm" c="dimmed" ta="center">
            {nextRetryIn != null
              ? `Retrying in ${nextRetryIn}s…`
              : retryCount > 0
                ? "Retrying…"
                : "Connection issue"}
          </Text>
          {retryCount > 1 && (
            <Text size="xs" c="dimmed" ta="center" opacity={0.6}>
              Attempt {retryCount}
            </Text>
          )}
        </Stack>
      </Center>
    );
  }

  if (!currentImage) {
    return null;
  }

  return (
    <Box
      h="100%"
      className={`unsplash-collection-block ${className || ""}`}
      style={{ position: "relative", overflow: "hidden" }}
      data-content-block-id={contentBlock.id}
      data-mode={mode}
      data-image-index={localIndex}
      data-items-total={items.length}
      data-has-more={hasMore}
    >
      <ContentBlockTransition
        transitionKey={currentImage.id}
        type={transitionType}
        duration={transitionDuration}
        className="unsplash-collection-block__transition"
      >
        <Box h="100%" style={{ position: "relative" }}>
          {!imageLoaded && (
            <Center
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                backgroundColor: "var(--mantine-color-dark-8, #1a1a1a)",
              }}
            >
              <LoadingIndicator size="md" />
            </Center>
          )}

          <img
            key={currentImage.id}
            src={currentImage.urls.regular}
            alt={
              currentImage.altDescription ||
              currentImage.description ||
              "Unsplash photo"
            }
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: imageLoaded ? 1 : 0,
              transition: "opacity 0.4s ease-in-out",
              display: "block",
            }}
          />

          {imageLoaded && currentImage.user && (
            <Box
              style={{
                position: "absolute",
                bottom: "var(--mantine-spacing-md)",
                right: "var(--mantine-spacing-md)",
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                borderRadius: "var(--mantine-radius-md)",
                padding: "var(--mantine-spacing-md)",
                display: "flex",
                alignItems: "center",
                gap: "var(--mantine-spacing-md)",
                maxWidth: 500,
              }}
            >
              {qrCodeDataUrl && (
                <Image
                  src={qrCodeDataUrl}
                  w={128}
                  h={128}
                  fit="contain"
                  style={{ flexShrink: 0 }}
                />
              )}
              <Box style={{ minWidth: 0 }}>
                {(currentImage.description || currentImage.altDescription) && (
                  <Text
                    size="md"
                    c="white"
                    lineClamp={2}
                    style={{ opacity: 0.95, fontWeight: 600, marginBottom: 4 }}
                  >
                    {currentImage.description || currentImage.altDescription}
                  </Text>
                )}
                <Text
                  size="md"
                  c="white"
                  style={{ opacity: 0.85, fontWeight: 500 }}
                >
                  Photo by {currentImage.user.name}
                </Text>
                <Text size="sm" c="white" style={{ opacity: 0.8 }}>
                  on Unsplash
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </ContentBlockTransition>
    </Box>
  );
};
